import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link as RouterLink } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Alert,
  CircularProgress,
  Grid,
  Chip,
  Breadcrumbs,
  Link,
  Tooltip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  RadioGroup,
  Radio,
  FormLabel,
  IconButton,
} from "@mui/material";
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  Preview as PreviewIcon,
  Add as AddIcon,
  AutoAwesome as AIIcon,
  ContentCopy as CopyIcon,
  DeleteOutline as DeleteOutlineIcon,
} from "@mui/icons-material";
import { useTopicById, useCreateTopic, useUpdateTopic } from "../hooks/useTopicForm";
import { useTags, useAddTag } from "../hooks/useTags";
import {
  OpinionFormData,
  TopicReference,
  QuestionType,
  GENRES,
  GENRE_LABELS,
  Genre,
  DIFFICULTIES,
  DIFFICULTY_LABELS,
  QUESTION_TYPES,
  QUESTION_TYPE_LABELS,
  QUESTION_TYPE_DESCRIPTIONS,
  CLAIM_TYPES,
  CLAIM_TYPE_LABELS,
  CLAIM_TYPE_DESCRIPTIONS,
} from "../types";
import OpinionPatternForm from "./common/OpinionPatternForm";
import ImageUpload from "./common/ImageUpload";
import { useAuth } from "../contexts/AuthContext";
import DifficultyChip from "./common/DifficultyChip";
import {
  generateThemePrompt,
  generateAttributeSuggestionPrompt,
  generateBackgroundPrompt,
  generateSourceTextPrompt,
  generateOpinionPatternPrompt,
  generateImagePrompt,
  generateImagePromptDesignPrompt,
  BACKGROUND_CHAR_GUIDE,
  BACKGROUND_OPENING_PATTERN_OPTIONS,
  BACKGROUND_CLOSING_PATTERN_OPTIONS,
} from "../utils/aiPromptGenerator";

// バリデーションスキーマ
const validationSchema = yup.object({
  genre: yup.string().required("ジャンルを選択してください"),
  difficulty: yup.number().required("難易度を選択してください").min(1).max(5),
  theme: yup
    .string()
    .required("テーマを入力してください")
    .max(100, "テーマは100文字以内で入力してください"),
  sourceText: yup.string().optional().max(1500, "課題文は1500文字以内で入力してください"),
  background: yup.string().optional().max(2000, "背景説明は2000文字以内で入力してください"),
  learnMore: yup
    .string()
    .optional()
    .max(6000, "テーマをもっと知るは6000文字以内で入力してください"),
  references: yup
    .array()
    .of(
      yup.object({
        title: yup.string().required("タイトルを入力してください"),
        url: yup.string().required("URLを入力してください").url("有効なURLを入力してください"),
      })
    )
    .optional(),
  opinionPatterns: yup
    .array()
    .of(
      yup.object({
        typeLabel: yup.string().required(),
        example: yup.string().required(),
        description: yup.string().required(),
      })
    )
    .required(),
  isPublished: yup.boolean().required(),
  // 賞味期限の有無。未判定のお題があるため必須にはしない
  isTopical: yup.boolean().optional(),
  tags: yup.array().of(yup.string().required()).required(),
  xPostUrl: yup
    .string()
    .optional()
    .url("有効なURLを入力してください")
    .test("x-url", "X(Twitter)のURLを入力してください", (value) => {
      if (!value) return true;
      return value.startsWith("https://x.com/") || value.startsWith("https://twitter.com/");
    }),
});

const TopicForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditing = Boolean(id);
  const { isAdmin } = useAuth();

  // データ取得・操作
  const { data: opinion, isLoading: isLoadingOpinion, error: loadError } = useTopicById(id);
  const createMutation = useCreateTopic();
  const updateMutation = useUpdateTopic(id);

  // タグマスター
  const { data: availableTags = [] } = useTags();
  const addTagMutation = useAddTag();

  // AIプロンプト
  const [aiPromptDialogOpen, setAiPromptDialogOpen] = useState(false);
  const [aiPromptTabIndex, setAiPromptTabIndex] = useState(0);
  const [aiPromptCopied, setAiPromptCopied] = useState(false);
  const [backgroundOpeningPatternId, setBackgroundOpeningPatternId] = useState<number | null>(null);
  const [backgroundClosingPatternId, setBackgroundClosingPatternId] = useState<number | null>(null);

  // フォーム状態
  const [previewMode, setPreviewMode] = useState(false);
  const [addTagDialogOpen, setAddTagDialogOpen] = useState(false);
  const [newTagInput, setNewTagInput] = useState("");
  const [currentImageUrl, setCurrentImageUrl] = useState<string | undefined>(undefined);

  // 画像URLの状態を更新（opinionから初期化）
  useEffect(() => {
    if (opinion?.imageUrl) {
      setCurrentImageUrl(opinion.imageUrl);
    }
  }, [opinion?.imageUrl]);

  // React Hook Form
  const {
    control,
    handleSubmit,
    watch,
    formState: { errors, isValid },
    reset,
  } = useForm<OpinionFormData>({
    resolver: yupResolver(validationSchema) as never,
    defaultValues: {
      genre: "",
      difficulty: 1,
      theme: "",
      sourceText: "",
      background: "",
      learnMore: "",
      references: [],
      opinionPatterns: [],
      isPublished: false,
      isTopical: undefined,
      tags: [],
      xPostUrl: "",
      questionType: undefined,
      claimType: undefined,
    },
    mode: "onChange",
  });

  const watchedValues = watch();

  // 編集時のデータ設定
  useEffect(() => {
    if (isEditing && opinion) {
      reset({
        genre: opinion.genre,
        difficulty: opinion.difficulty,
        theme: opinion.theme,
        sourceText: opinion.sourceText || "",
        background: opinion.background || "",
        learnMore: opinion.learnMore || "",
        references: opinion.references || [],
        opinionPatterns: opinion.opinionPatterns || [],
        isPublished: opinion.isPublished,
        isTopical: opinion.isTopical,
        tags: opinion.tags || [],
        xPostUrl: opinion.xPostUrl || "",
        questionType: opinion.questionType,
        claimType: opinion.claimType,
      });
    }
  }, [isEditing, opinion, reset]);

  // フォーム送信
  const onSubmit = async (data: OpinionFormData) => {
    try {
      if (isEditing) {
        await updateMutation.mutateAsync(data);
      } else {
        await createMutation.mutateAsync(data);
      }
    } catch (error) {
      console.error("Save error:", error);
    }
  };

  // AIプロンプト生成
  const aiPrompts = [
    generateThemePrompt({
      genre: watchedValues.genre || "",
      difficulty: watchedValues.difficulty,
      questionType: watchedValues.questionType || "claim",
      claimType: watchedValues.claimType || undefined,
    }),
    generateAttributeSuggestionPrompt({
      theme: watchedValues.theme || "",
    }),
    generateBackgroundPrompt({
      theme: watchedValues.theme || "",
      genre: watchedValues.genre || "",
      difficulty: watchedValues.difficulty,
      questionType: watchedValues.questionType || "claim",
      claimType: watchedValues.claimType || undefined,
      openingPatternId: backgroundOpeningPatternId ?? undefined,
      closingPatternId: backgroundClosingPatternId ?? undefined,
    }),
    generateSourceTextPrompt({
      theme: watchedValues.theme || "",
      genre: watchedValues.genre || "",
      difficulty: watchedValues.difficulty,
      claimType: watchedValues.claimType || undefined,
      background: watchedValues.background || undefined,
    }),
    generateOpinionPatternPrompt({
      theme: watchedValues.theme || "",
      genre: watchedValues.genre || "",
      difficulty: watchedValues.difficulty,
      questionType: watchedValues.questionType || "claim",
      claimType: watchedValues.claimType || undefined,
      background: watchedValues.background || undefined,
      sourceText: watchedValues.sourceText || undefined,
    }),
    generateImagePromptDesignPrompt({
      theme: watchedValues.theme || "",
      background: watchedValues.background || undefined,
      sourceText: watchedValues.sourceText || undefined,
    }),
    generateImagePrompt({
      theme: watchedValues.theme || "",
    }),
  ];

  const aiPromptLabels = [
    "A: テーマ候補",
    "B: 属性提案",
    "C: 背景",
    "D: 課題文（任意）",
    "E: 意見パターン",
    "F: 画像プロンプト設計",
    "G: 画像生成",
  ];

  const handleCopyAiPrompt = async () => {
    try {
      await navigator.clipboard.writeText(aiPrompts[aiPromptTabIndex]);
      setAiPromptCopied(true);
      setTimeout(() => setAiPromptCopied(false), 2000);
    } catch (error) {
      console.error("Failed to copy:", error);
    }
  };

  // ローディング状態
  if (isEditing && isLoadingOpinion) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  // エラー状態
  if (loadError) {
    return (
      <Alert severity="error">
        データの読み込みに失敗しました:{" "}
        {loadError instanceof Error ? loadError.message : "Unknown error"}
      </Alert>
    );
  }

  const isSubmitting = createMutation.isLoading || updateMutation.isLoading;

  return (
    <Box>
      <>
        {/* パンくずリスト */}
        <Breadcrumbs sx={{ mb: 2 }}>
          <Link component={RouterLink} to="/opinions" color="inherit">
            問題一覧
          </Link>
          <Typography color="text.primary">{isEditing ? "問題編集" : "新規作成"}</Typography>
        </Breadcrumbs>

        {/* ヘッダー */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4">{isEditing ? "問題編集" : "新規問題作成"}</Typography>
          <Box display="flex" gap={1}>
            <Button
              variant="outlined"
              startIcon={<AIIcon />}
              onClick={() => setAiPromptDialogOpen(true)}
            >
              AIプロンプト
            </Button>
            <Button
              variant="outlined"
              startIcon={<PreviewIcon />}
              onClick={() => setPreviewMode(!previewMode)}
            >
              {previewMode ? "編集モード" : "プレビュー"}
            </Button>
          </Box>
        </Box>

        {previewMode ? (
          // プレビューモード
          <Card>
            <CardContent>
              <Box display="flex" gap={1} mb={2}>
                <DifficultyChip difficulty={watchedValues.difficulty} size="small" />
                <Chip
                  label={
                    GENRE_LABELS[watchedValues.genre as Genre] ||
                    watchedValues.genre ||
                    "（ジャンル未選択）"
                  }
                  color="secondary"
                  size="small"
                />
                <Chip
                  label={watchedValues.isPublished ? "公開" : "非公開"}
                  color={watchedValues.isPublished ? "success" : "default"}
                  size="small"
                />
              </Box>

              {/* テーマ */}
              {watchedValues.theme && (
                <Typography variant="h6" gutterBottom>
                  {watchedValues.theme}
                </Typography>
              )}

              {watchedValues.sourceText ? (
                <Box mb={2}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    課題文（sourceText）
                  </Typography>
                  <Typography variant="body2">{watchedValues.sourceText}</Typography>
                </Box>
              ) : null}

              {watchedValues.background && (
                <Box mb={2}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    背景説明
                  </Typography>
                  <Typography variant="body2">{watchedValues.background}</Typography>
                </Box>
              )}

              {watchedValues.opinionPatterns.length > 0 && (
                <Box mt={3}>
                  <Typography variant="h6" gutterBottom>
                    意見パターン
                  </Typography>
                  {watchedValues.opinionPatterns.map((pattern, index) => (
                    <Box key={index} mb={2}>
                      <Typography variant="subtitle2">{pattern.typeLabel}</Typography>
                      <Typography variant="body2">{pattern.example}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {pattern.description}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </CardContent>
          </Card>
        ) : (
          // 編集モード
          <form onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={3}>
              {/* 基本情報 */}
              <Grid size={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      基本情報
                    </Typography>

                    <Grid container spacing={2}>
                      <Grid size={12}>
                        <Controller
                          name="theme"
                          control={control}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              label="テーマ *"
                              fullWidth
                              error={!!errors.theme}
                              helperText={
                                errors.theme?.message || `${field.value?.length || 0}/100文字`
                              }
                            />
                          )}
                        />
                      </Grid>

                      <Grid size={{ xs: 12, sm: 6 }}>
                        <FormControl fullWidth error={!!errors.genre}>
                          <InputLabel>ジャンル *</InputLabel>
                          <Controller
                            name="genre"
                            control={control}
                            render={({ field }) => (
                              <Select {...field} label="ジャンル *">
                                {GENRES.map((genre) => (
                                  <MenuItem key={genre} value={genre}>
                                    {GENRE_LABELS[genre]}
                                  </MenuItem>
                                ))}
                              </Select>
                            )}
                          />
                          {errors.genre && (
                            <Typography variant="caption" color="error">
                              {errors.genre.message}
                            </Typography>
                          )}
                        </FormControl>
                      </Grid>

                      <Grid size={{ xs: 12, sm: 6 }}>
                        <FormControl fullWidth error={!!errors.difficulty}>
                          <InputLabel>難易度 *</InputLabel>
                          <Controller
                            name="difficulty"
                            control={control}
                            render={({ field }) => (
                              <Select {...field} label="難易度 *">
                                {DIFFICULTIES.map((diff) => (
                                  <MenuItem key={diff} value={diff}>
                                    難易度{diff} - {DIFFICULTY_LABELS[diff]}
                                  </MenuItem>
                                ))}
                              </Select>
                            )}
                          />
                          {errors.difficulty ? (
                            <Typography variant="caption" color="error">
                              {errors.difficulty.message}
                            </Typography>
                          ) : (
                            <Typography variant="caption" color="text.secondary">
                              採点には影響しません。テーマの重さと、背景文や課題文の分量の目安に使います
                            </Typography>
                          )}
                        </FormControl>
                      </Grid>

                      {/* 問題タイプ */}
                      <Grid size={12}>
                        <FormControl component="fieldset">
                          <FormLabel component="legend">問題タイプ</FormLabel>
                          <Controller
                            name="questionType"
                            control={control}
                            render={({ field }) => (
                              <RadioGroup
                                value={field.value || ""}
                                onChange={(e) => {
                                  const value = e.target.value as QuestionType;
                                  field.onChange(value);
                                }}
                              >
                                {QUESTION_TYPES.map((type) => (
                                  <FormControlLabel
                                    key={type}
                                    value={type}
                                    control={<Radio />}
                                    label={`${QUESTION_TYPE_LABELS[type]} — ${QUESTION_TYPE_DESCRIPTIONS[type].description}（例: ${QUESTION_TYPE_DESCRIPTIONS[type].example}）`}
                                  />
                                ))}
                              </RadioGroup>
                            )}
                          />
                        </FormControl>
                      </Grid>

                      {/* 主張タイプ（Claim選択時のみ） */}
                      {watchedValues.questionType === "claim" && (
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <FormControl fullWidth>
                            <InputLabel>主張タイプ</InputLabel>
                            <Controller
                              name="claimType"
                              control={control}
                              render={({ field }) => (
                                <Select {...field} label="主張タイプ" value={field.value || ""}>
                                  {CLAIM_TYPES.map((type) => (
                                    <MenuItem key={type} value={type}>
                                      {CLAIM_TYPE_LABELS[type]} —{" "}
                                      {CLAIM_TYPE_DESCRIPTIONS[type].description}（例:{" "}
                                      {CLAIM_TYPE_DESCRIPTIONS[type].example}）
                                    </MenuItem>
                                  ))}
                                </Select>
                              )}
                            />
                          </FormControl>
                        </Grid>
                      )}

                      {/* 背景説明（任意） */}
                      <Grid size={12}>
                        <Controller
                          name="background"
                          control={control}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              value={field.value || ""}
                              label="背景説明（任意）"
                              fullWidth
                              multiline
                              rows={4}
                              placeholder="このトピックの背景や文脈を補足する情報を入力してください"
                              error={!!errors.background}
                              helperText={
                                errors.background?.message ||
                                (() => {
                                  const guide = BACKGROUND_CHAR_GUIDE[watchedValues.difficulty];
                                  const guideText = guide
                                    ? ` — 難易度${watchedValues.difficulty}の推奨: ${guide.min}〜${guide.max}文字`
                                    : "";
                                  return `${(field.value || "").length}/2000文字${guideText}`;
                                })()
                              }
                            />
                          )}
                        />
                      </Grid>

                      {/* 課題文（任意・新形式: sourceText） */}
                      <Grid size={12}>
                        <Controller
                          name="sourceText"
                          control={control}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              value={field.value || ""}
                              label="課題文（任意）"
                              fullWidth
                              multiline
                              rows={6}
                              placeholder="入力すると課題文を読んでから回答する形式（課題文型）になります"
                              error={!!errors.sourceText}
                              helperText={
                                errors.sourceText?.message ||
                                `${(field.value || "").length}/1500文字。入力すると出題形式が「課題文型（readWrite）」に切り替わります。新規の課題文はこちらに入力してください`
                              }
                            />
                          )}
                        />
                      </Grid>

                      {/* テーマをもっと知る（learnMore、任意・v2.3。旧称「深掘り解説」） */}
                      <Grid size={12}>
                        <Controller
                          name="learnMore"
                          control={control}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              value={field.value || ""}
                              label="テーマをもっと知る（任意）"
                              fullWidth
                              multiline
                              rows={8}
                              placeholder="テーマをもっと知りたい人向けの解説を入力してください（結果画面と履歴詳細で折りたたみ表示。目安800〜1900字、無理に書かなくてよい）"
                              error={!!errors.learnMore}
                              helperText={
                                errors.learnMore?.message ||
                                `${(field.value || "").length}/6000文字。表示専用で、採点には使われません`
                              }
                            />
                          )}
                        />
                      </Grid>

                      {/* 参考URL（任意・v2.3） */}
                      <Grid size={12}>
                        <Controller
                          name="references"
                          control={control}
                          render={({ field }) => {
                            const topicReferences: TopicReference[] = field.value || [];
                            const updateReference = (
                              index: number,
                              key: keyof TopicReference,
                              value: string
                            ) => {
                              const updated = topicReferences.map((reference, i) =>
                                i === index ? { ...reference, [key]: value } : reference
                              );
                              field.onChange(updated);
                            };
                            return (
                              <Box>
                                <Typography variant="subtitle2" gutterBottom>
                                  参考URL（任意）
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  display="block"
                                  sx={{ mb: 1 }}
                                >
                                  「テーマをもっと知る」の出典など、テーマを詳しく知るためのリンクです。実在するページのURLを設定してください
                                </Typography>
                                {topicReferences.map((reference, index) => (
                                  <Box
                                    key={index}
                                    display="flex"
                                    gap={1}
                                    alignItems="flex-start"
                                    mb={1}
                                  >
                                    <TextField
                                      label="タイトル"
                                      size="small"
                                      value={reference.title}
                                      onChange={(e) =>
                                        updateReference(index, "title", e.target.value)
                                      }
                                      sx={{ flex: 1 }}
                                    />
                                    <TextField
                                      label="URL"
                                      size="small"
                                      value={reference.url}
                                      onChange={(e) =>
                                        updateReference(index, "url", e.target.value)
                                      }
                                      placeholder="https://..."
                                      sx={{ flex: 2 }}
                                    />
                                    <IconButton
                                      aria-label="この参考URLを削除"
                                      onClick={() =>
                                        field.onChange(
                                          topicReferences.filter((_, i) => i !== index)
                                        )
                                      }
                                    >
                                      <DeleteOutlineIcon />
                                    </IconButton>
                                  </Box>
                                ))}
                                <Button
                                  startIcon={<AddIcon />}
                                  size="small"
                                  onClick={() =>
                                    field.onChange([...topicReferences, { title: "", url: "" }])
                                  }
                                >
                                  参考URLを追加
                                </Button>
                              </Box>
                            );
                          }}
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              {/* 意見パターン */}
              <Grid size={12}>
                <Card>
                  <CardContent>
                    <Controller
                      name="opinionPatterns"
                      control={control}
                      render={({ field }) => (
                        <OpinionPatternForm
                          patterns={field.value}
                          onChange={field.onChange}
                          difficulty={watchedValues.difficulty}
                          error={errors.opinionPatterns?.message}
                        />
                      )}
                    />
                  </CardContent>
                </Card>
              </Grid>

              {/* 設定・タグ */}
              <Grid size={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      設定・タグ
                    </Typography>

                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Controller
                          name="isPublished"
                          control={control}
                          render={({ field }) => (
                            <Tooltip
                              title={!isAdmin ? "公開設定は管理者のみが変更できます" : ""}
                              placement="right"
                            >
                              <FormControlLabel
                                control={
                                  <Switch {...field} checked={field.value} disabled={!isAdmin} />
                                }
                                label={field.value ? "公開中" : "非公開"}
                              />
                            </Tooltip>
                          )}
                        />
                      </Grid>

                      {/* 賞味期限の有無。過去問として再掲してよいかの判断に使う */}
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <FormControl fullWidth>
                          <InputLabel>賞味期限</InputLabel>
                          <Controller
                            name="isTopical"
                            control={control}
                            render={({ field }) => (
                              <Select
                                label="賞味期限"
                                value={field.value === undefined ? "" : String(field.value)}
                                onChange={(event) =>
                                  field.onChange(
                                    event.target.value === ""
                                      ? undefined
                                      : event.target.value === "true"
                                  )
                                }
                              >
                                <MenuItem value="">未判定</MenuItem>
                                <MenuItem value="false">
                                  普遍 — いつ出しても成立する（過去問として再掲してよい）
                                </MenuItem>
                                <MenuItem value="true">
                                  時事 — 時間が経つと問いが成立しなくなる
                                </MenuItem>
                              </Select>
                            )}
                          />
                        </FormControl>
                      </Grid>
                    </Grid>

                    <Divider sx={{ my: 2 }} />

                    {/* X投稿URL */}
                    <Typography variant="subtitle1" gutterBottom>
                      X投稿URL
                    </Typography>
                    <Controller
                      name="xPostUrl"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="X投稿URL"
                          fullWidth
                          placeholder="https://x.com/..."
                          error={!!errors.xPostUrl}
                          helperText={
                            errors.xPostUrl?.message ||
                            "結果画面に「他の人の意見を見てみる」リンクが表示されます"
                          }
                          size="small"
                          sx={{ mb: 2 }}
                        />
                      )}
                    />

                    <Divider sx={{ my: 2 }} />

                    {/* タグ */}
                    <Typography variant="subtitle1" gutterBottom>
                      タグ
                    </Typography>
                    <Controller
                      name="tags"
                      control={control}
                      render={({ field }) => (
                        <Box display="flex" gap={1} alignItems="center">
                          <FormControl size="small" sx={{ width: 200 }}>
                            <InputLabel>タグを選択</InputLabel>
                            <Select
                              {...field}
                              multiple
                              label="タグを選択"
                              renderValue={(selected) => (
                                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                  {(selected as string[]).map((value) => (
                                    <Chip key={value} label={value} size="small" />
                                  ))}
                                </Box>
                              )}
                            >
                              {availableTags.map((tag) => (
                                <MenuItem key={tag.id} value={tag.name}>
                                  {tag.name}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => setAddTagDialogOpen(true)}
                            sx={{ height: 40 }}
                          >
                            <AddIcon fontSize="small" />
                          </Button>
                        </Box>
                      )}
                    />
                  </CardContent>
                </Card>
              </Grid>

              {/* イメージ画像（編集時のみ表示） */}
              {isEditing && id && (
                <Grid size={12}>
                  <ImageUpload
                    opinionId={id}
                    currentImageUrl={currentImageUrl}
                    onImageUpdate={(imageUrl) => setCurrentImageUrl(imageUrl || undefined)}
                  />
                </Grid>
              )}

              {/* 保存・キャンセルボタン */}
              <Grid size={12}>
                <Box display="flex" gap={2} justifyContent="flex-end">
                  <Button
                    variant="outlined"
                    startIcon={<CancelIcon />}
                    onClick={() => navigate("/opinions")}
                    disabled={isSubmitting}
                  >
                    キャンセル
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    startIcon={<SaveIcon />}
                    disabled={isSubmitting || !isValid}
                  >
                    {isSubmitting ? "保存中..." : "保存"}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </form>
        )}

        {/* エラー表示 */}
        {(createMutation.error || updateMutation.error) && (
          <Alert severity="error" sx={{ mt: 2 }}>
            保存に失敗しました:{" "}
            {(createMutation.error as Error)?.message ||
              (updateMutation.error as Error)?.message ||
              "Unknown error"}
          </Alert>
        )}

        {/* 新規タグ追加ダイアログ */}
        <Dialog open={addTagDialogOpen} onClose={() => setAddTagDialogOpen(false)}>
          <DialogTitle>新しいタグを追加</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="タグ名"
              fullWidth
              value={newTagInput}
              onChange={(e) => setNewTagInput(e.target.value)}
              onKeyDown={async (e) => {
                if (e.key === "Enter" && newTagInput.trim()) {
                  e.preventDefault();
                  await addTagMutation.mutateAsync(newTagInput.trim());
                  setNewTagInput("");
                  setAddTagDialogOpen(false);
                }
              }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAddTagDialogOpen(false)}>キャンセル</Button>
            <Button
              onClick={async () => {
                if (newTagInput.trim()) {
                  await addTagMutation.mutateAsync(newTagInput.trim());
                  setNewTagInput("");
                  setAddTagDialogOpen(false);
                }
              }}
              disabled={!newTagInput.trim() || addTagMutation.isLoading}
            >
              {addTagMutation.isLoading ? <CircularProgress size={20} /> : "追加"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* AIプロンプトダイアログ */}
        <Dialog
          open={aiPromptDialogOpen}
          onClose={() => setAiPromptDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <Box display="flex" alignItems="center" gap={1}>
              <AIIcon />
              AIプロンプト
            </Box>
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              プロンプトをコピーしてChatGPTなどのAIに送信してください。
            </Typography>

            <Tabs
              value={aiPromptTabIndex}
              onChange={(_, newValue) => {
                setAiPromptTabIndex(newValue);
                setAiPromptCopied(false);
              }}
              variant="scrollable"
              scrollButtons="auto"
              sx={{ mb: 2 }}
            >
              {aiPromptLabels.map((label, index) => (
                <Tab key={index} label={label} />
              ))}
            </Tabs>

            {aiPromptTabIndex === 2 && (
              <>
                <Box
                  sx={{
                    mb: 2,
                    p: 2,
                    border: "1px solid",
                    borderColor: "grey.300",
                    borderRadius: 1,
                  }}
                >
                  <FormLabel sx={{ fontSize: "0.85rem", fontWeight: "bold" }}>
                    導入の型（任意。未選択ならAIが候補から選ぶ）
                  </FormLabel>
                  <RadioGroup
                    value={backgroundOpeningPatternId ?? ""}
                    onChange={(e) => {
                      const value = e.target.value;
                      setBackgroundOpeningPatternId(value === "" ? null : Number(value));
                      setAiPromptCopied(false);
                    }}
                    sx={{ mt: 1 }}
                  >
                    <FormControlLabel
                      value=""
                      control={<Radio size="small" />}
                      label="AIに任せる"
                      sx={{ "& .MuiFormControlLabel-label": { fontSize: "0.85rem" } }}
                    />
                    {BACKGROUND_OPENING_PATTERN_OPTIONS.map((option) => (
                      <FormControlLabel
                        key={option.id}
                        value={option.id}
                        control={<Radio size="small" />}
                        label={`${option.label}（${option.description}）`}
                        sx={{ "& .MuiFormControlLabel-label": { fontSize: "0.85rem" } }}
                      />
                    ))}
                  </RadioGroup>
                </Box>

                <Box
                  sx={{
                    mb: 2,
                    p: 2,
                    border: "1px solid",
                    borderColor: "grey.300",
                    borderRadius: 1,
                  }}
                >
                  <FormLabel sx={{ fontSize: "0.85rem", fontWeight: "bold" }}>
                    締めの型（任意。未選択ならAIが候補から選ぶ）
                  </FormLabel>
                  <RadioGroup
                    value={backgroundClosingPatternId ?? ""}
                    onChange={(e) => {
                      const value = e.target.value;
                      setBackgroundClosingPatternId(value === "" ? null : Number(value));
                      setAiPromptCopied(false);
                    }}
                    sx={{ mt: 1 }}
                  >
                    <FormControlLabel
                      value=""
                      control={<Radio size="small" />}
                      label="AIに任せる"
                      sx={{ "& .MuiFormControlLabel-label": { fontSize: "0.85rem" } }}
                    />
                    {BACKGROUND_CLOSING_PATTERN_OPTIONS.map((option) => (
                      <FormControlLabel
                        key={option.id}
                        value={option.id}
                        control={<Radio size="small" />}
                        label={`${option.label}（${option.description}）`}
                        sx={{ "& .MuiFormControlLabel-label": { fontSize: "0.85rem" } }}
                      />
                    ))}
                  </RadioGroup>
                </Box>
              </>
            )}

            <Box
              sx={{
                p: 2,
                bgcolor: "grey.100",
                borderRadius: 1,
                maxHeight: 400,
                overflow: "auto",
                fontFamily: "monospace",
                fontSize: "0.85rem",
                whiteSpace: "pre-wrap",
              }}
            >
              {aiPrompts[aiPromptTabIndex]}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAiPromptDialogOpen(false)}>閉じる</Button>
            <Button
              variant="contained"
              startIcon={<CopyIcon />}
              onClick={handleCopyAiPrompt}
              color={aiPromptCopied ? "success" : "primary"}
            >
              {aiPromptCopied ? "コピーしました" : "コピー"}
            </Button>
          </DialogActions>
        </Dialog>
      </>
    </Box>
  );
};

export default TopicForm;
